import { castArray, isArray, mapKeys, mapValues, memoize, pick, without } from 'lodash-es';
import { RouterPageElement } from '@adobe/edex/ui/components/base.element';

import { selectLocationSearch, selectMetadata, selectSearch } from '@adobe/edex/ui/store/selectors';
import { SearchState } from '@adobe/edex/ui/store/reducers/search';
import {
  fetchMetadata,
  searchCourses,
  searchDiscussions,
  searchResources,
  searchCollections,
} from '@adobe/edex/ui/store/routines';
import { SearchEntityType, SearchKey, SearchParams, SearchSort, SearchType } from '@adobe/edex/ui/shared/types/search';
import { router } from '@adobe/edex/ui/config/router';
import { buildLink, getQueryParams } from '@adobe/edex/ui/shared/utils/routes';
import '@adobe/edex/ui/components/shared/tree-selector/tree-selector';
import { MetadataState } from '@adobe/edex/ui/store/reducers/api/metadata';
import {
  CourseTypeID,
  EdexDomID,
  Locale,
  ResourceTypeI18nLabel,
  TARGET_TRIGGER_VIEW,
} from '@adobe/edex/ui/shared/constants';
import {
  equalsSearchParentType,
  normalizeSelectedMetadataItems,
  parseSearchRouteParams,
  searchRedirects,
  isSearchParamChanged,
  isExpressProduct,
} from '@adobe/edex/ui/components/search-page/search.utils';
import {
  ADOBE_EXPRESS,
  LESSON_PLANS,
  SEARCH_ENTITY_TYPE_2_SEARCH_TYPES,
  SEARCH_FILTER_METADATA_TYPES,
  SEARCH_PARENT_TYPES,
  SEARCH_TYPE_2_ENTITY,
} from '@adobe/edex/ui/components/search-page/constants';
import { getCurrentLocale } from '@adobe/edex/ui/shared/config/i18n';
import { EdexState } from '../../store';
import { Router } from '@vaadin/router';
import { countAllAPI } from '../../api';
import { DurationSchema, MetadataModelKey } from '../../types';
import { I18nKey } from '@adobe/edex/ui/shared/constants/i18n';
import { SHOW_ALL } from '../../constants';

const ROWS_PER_PAGE = 6;

export abstract class SearchPageBase extends RouterPageElement {
  get useLightDom() {
    return true;
  }

  static get properties() {
    return {
      ...super.properties,
      search: { attribute: false },
      locationSearch: { attribute: false },
      metadata: { attribute: false },
      showFilters: { attribute: true },
      makeAPIqueryCall: { attribute: false },
      resultsByType: { attribute: false },
    };
  }
  search: SearchState;
  locationSearch: string;
  metadata: MetadataState['byType'];
  showFilters = true;
  makeAPIqueryCall = true;
  resultsByType = {
    [SearchType.collections]: null,
    [SearchType.projects]: null,
    [SearchType.supplemental]: null,
    [SearchType.teachingResources]: null,
    [SearchType.selfPacedCourses]: null,
    [SearchType.toolkits]: null,
    [SearchType.professionalLearning]: null,
    [SearchType.discussions]: null,
  };

  actions = {
    searchResources: searchResources.trigger,
    searchCourses: searchCourses.trigger,
    searchDiscussions: searchDiscussions.trigger,
    fetchMetadata: fetchMetadata.trigger,
    searchCollections: searchCollections.trigger,
  };

  defaultLimit = {
    [SearchEntityType.resources]: 4,
    [SearchEntityType.courses]: 2,
    [SearchEntityType.discussions]: 8,
    [SearchEntityType.collections]: 4,
  };

  defaultResultsPerRow = {
    [SearchEntityType.resources]: 4,
    [SearchEntityType.courses]: 2,
    [SearchEntityType.discussions]: 4,
    [SearchEntityType.collections]: 4,
  };

  METADATA_DURATION: DurationSchema[] = [
    {
      id: '1',
      name: '30 Minutes',
      i18nLabel: I18nKey.thirtyMinutes,
      urlLabel: '0.5',
    },
    {
      id: '2',
      name: '1 Hour',
      i18nLabel: I18nKey.oneHour,
      urlLabel: '1',
    },
    {
      id: '3',
      name: '2 Hours',
      i18nLabel: I18nKey.twoHours,
      urlLabel: '2',
    },
    {
      id: '4',
      name: '3 Hours',
      i18nLabel: I18nKey.threeHours,
      urlLabel: '3',
    },
  ];

  fetchMetadata() {
    const types = SEARCH_FILTER_METADATA_TYPES.filter((ty) => ty !== MetadataModelKey.duration);
    this.actions.fetchMetadata({
      type: types.join(','),
    });
    this.metadata.duration = this.METADATA_DURATION;
  }

  async fetchCountAll() {
    if (this.makeAPIqueryCall) {
      try {
        const params = new URLSearchParams([['q', this.query]]);
        this.resultsByType = await countAllAPI.get('', {
          params,
        });
        this.resultsByType = this.resultsByType['data'];
      } catch (e) {
        console.log(e);
      } finally {
        this.makeAPIqueryCall = false;
      }
    }
  }

  isMetadataLoaded(metadata = this.metadata): boolean {
    return SEARCH_FILTER_METADATA_TYPES.every((type) => metadata?.[type]);
  }

  searchRedirecting() {
    searchRedirects.forEach((redirect) => {
      if (this.locationSearch.match(redirect.regex)) {
        Router.go({
          pathname: buildLink('/search'),
          search: this.locationSearch.replace(redirect.regex, redirect.redirectedURL),
        });
      }
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.searchRedirecting();
    if (window.matchMedia('(max-width: 599px)').matches) {
      this.showFilters = !this.showFilters; // hide filters onload if mobile device
    }
    window.adobe?.target.triggerView(TARGET_TRIGGER_VIEW.searchPage);
  }

  get searchEntityTypes(): SearchEntityType[] {
    let entityTypes = Object.values(SearchEntityType);
    const locale = getCurrentLocale();
    if (locale === Locale.de) {
      entityTypes = without(entityTypes, SearchEntityType.courses);
    }
    if (locale === Locale.de || locale === Locale.jp) {
      entityTypes = without(entityTypes, SearchEntityType.collections);
    }
    if (entityTypes.includes(SearchEntityType.discussions)) {
      entityTypes = without(entityTypes, SearchEntityType.discussions);
    }
    return entityTypes;
  }

  get query() {
    return this.searchParams[SearchKey.query];
  }

  get entityType(): SearchEntityType {
    const { searchParams } = this;
    const [selectedType] = searchParams[SearchKey.type];
    return selectedType ? SEARCH_TYPE_2_ENTITY[selectedType] : null;
  }

  showContent = (entityType: SearchEntityType): boolean => {
    return this.showAll || this.entityType === entityType;
  };

  get entityType2Action() {
    return new Map([
      [SearchEntityType.resources, this.actions.searchResources],
      [SearchEntityType.courses, this.actions.searchCourses],
      [SearchEntityType.collections, this.actions.searchCollections],
    ]);
  }

  get showAll(): boolean {
    const { searchParams } = this;
    return searchParams[SearchKey.type].length === 0;
  }

  isCollections(): boolean {
    const { searchParams } = this;
    return (
      searchParams[SearchKey.type] &&
      searchParams[SearchKey.type].length > 0 &&
      searchParams[SearchKey.type][0] === SearchEntityType.collections
    );
  }

  isResource(): boolean {
    const { searchParams } = this;
    return (
      searchParams[SearchKey.type] &&
      searchParams[SearchKey.type].length > 0 &&
      (searchParams[SearchKey.type][0] === SearchType.teachingResources ||
        searchParams[SearchKey.type][0] === SearchType.projects ||
        searchParams[SearchKey.type][0] === SearchType.supplemental)
    );
  }

  handleLesssonPlansType = (params) => {
    if (isArray(params.type)) {
      params.type = params.type.filter((item) => item !== LESSON_PLANS);
    } else if (params.type === LESSON_PLANS) {
      params.type = '';
    }
  };

  handleTemplateFilterQuery = () => {
    const params: SearchParams = this.searchRouteParams || this.rawQueryParams;
    if (isArray(params.product)) {
      if (params.product.indexOf(ADOBE_EXPRESS) <= -1) {
        params.template = SHOW_ALL;
      }
    } else {
      if (params.product) {
        if (params.product !== ADOBE_EXPRESS) {
          params.template = SHOW_ALL;
        }
      } else {
        params.template = SHOW_ALL;
      }
    }
  };

  get searchRouteParams() {
    return this.isMetadataLoaded() ? parseSearchRouteParams(router.location.params, this.metadata) : null;
  }

  get rawQueryParams() {
    return getQueryParams(this.locationSearch);
  }

  get searchParams() {
    const params: SearchParams = this.searchRouteParams || this.rawQueryParams;
    this.handleLesssonPlansType(params);
    return {
      [SearchKey.query]: params[SearchKey.query] || '',
      [SearchKey.page]: +params[SearchKey.page] || 1,
      [SearchKey.sort]: params[SearchKey.sort] || SearchSort.relevance,
      [SearchKey.type]: castArray(params[SearchKey.type] || []),
      [SearchKey.product]: castArray(params[SearchKey.product] || []),
      [SearchKey.subject]: castArray(params[SearchKey.subject] || []),
      [SearchKey.ageLevel]: castArray(params[SearchKey.ageLevel] || []),
      [SearchKey.time]: castArray(params[SearchKey.time] || []),
      [SearchKey.duration]: castArray(params[SearchKey.duration] || []),
      [SearchKey.template]: params[SearchKey.template] || SHOW_ALL,
      [SearchKey.author]: castArray(params[SearchKey.author] || []),
    };
  }

  get page() {
    return this.searchParams[SearchKey.page];
  }

  get totalPages(): number {
    const {
      search: {
        [this.entityType]: {
          meta: { totalRecords, limit },
        },
      },
    } = this;
    return Math.ceil(totalRecords / limit);
  }

  getLimit(entityType: SearchEntityType): number {
    return this.showAll ? this.defaultLimit[entityType] : this.defaultResultsPerRow[entityType] * ROWS_PER_PAGE;
  }

  /**
   * Some subjects belong to multiple parents, eg History
   * TODO: confirm with Matt
   */
  getSubjectParentIDs = memoize((i18nLabel: string) =>
    this.metadata.subjects
      .filter((item) => item.children?.some((c) => c.i18nLabel === i18nLabel))
      .map(({ i18nLabel }) => i18nLabel)
  );

  stateChanged(state: EdexState) {
    super.stateChanged(state);
    this.search = selectSearch(state);
    const queryParamsOld = getQueryParams(this.locationSearch);
    this.locationSearch = selectLocationSearch(state);
    const queryParamsNew = getQueryParams(this.locationSearch);
    if (isSearchParamChanged(queryParamsOld, queryParamsNew, SearchKey.query)) {
      this.makeAPIqueryCall = true;
    }
    this.metadata = selectMetadata(state);
  }

  updateSearch(params: SearchParams) {
    const searchParentTypeChanged =
      params[SearchKey.type] &&
      !equalsSearchParentType(this.searchParams[SearchKey.type][0], params[SearchKey.type][0]);
    this.navigate({
      path: '/search',
      queryParams: searchParentTypeChanged
        ? {
            [SearchKey.query]: this.query, // preserve `query`
            ...params,
          }
        : {
            ...this.searchRouteParams, // preserve canonical url filters (if any)
            [SearchKey.page]: null, // reset page on any change
            ...params,
          },
      mergeQueryParams: !searchParentTypeChanged, // reset filters on search type change
    });
    this.resetResultsScrollPosition();
  }

  resetResultsScrollPosition() {
    document.querySelector(`#${EdexDomID.searchResults}`)?.scrollTo?.({
      top: 0,
      behavior: 'smooth',
    });
  }

  paginate(page: number) {
    this.updateSearch({ [SearchKey.page]: page });
  }

  toggleFilterView() {
    this.showFilters = !this.showFilters;
  }

  getSearchKeysByType(entityType: SearchEntityType): SearchKey[] {
    // common keys
    const keys = [SearchKey.query, SearchKey.sort, SearchKey.type, SearchKey.product, SearchKey.duration];
    switch (entityType) {
      case SearchEntityType.resources:
        keys.push(SearchKey.subject, SearchKey.ageLevel, SearchKey.time, SearchKey.template, SearchKey.author);
        break;
      case SearchEntityType.courses:
        break;
      case SearchEntityType.discussions:
        break;
      case SearchEntityType.collections:
        keys.push(SearchKey.subject, SearchKey.ageLevel);
        break;
    }
    return keys;
  }

  getTemplatePayload(payload) {
    if (payload.template === SHOW_ALL || !isExpressProduct(payload?.product)) {
      return;
    }
    return true;
  }

  getSearchPayloadByType(entityType: SearchEntityType) {
    const { searchParams, page, showAll, metadata } = this;
    this.handleTemplateFilterQuery();
    const limit = this.getLimit(entityType);
    const offset = showAll ? 0 : (page - 1) * limit;
    let payload = pick(searchParams, this.getSearchKeysByType(entityType));

    // modify values
    payload = mapValues(payload, (value, key) => {
      switch (key) {
        case SearchKey.query:
          return value || undefined;
        case SearchKey.type:
          // 1. exclude parent type (virtual)
          value = without(value, ...SEARCH_PARENT_TYPES);
          // 2. apply default type filters (when no sub type is selected)
          value = value.length ? value : SEARCH_ENTITY_TYPE_2_SEARCH_TYPES.get(entityType);
          return value.map(
            (value) =>
              ({
                // replace course type query param with course type id
                [SearchType.selfPacedCourses]: CourseTypeID.selfPaced,
                [SearchType.toolkits]: CourseTypeID.toolkit,
                // replace resource type query param with resource type i18nLabel
                [SearchType.projects]: ResourceTypeI18nLabel.project,
                [SearchType.supplemental]: ResourceTypeI18nLabel.supplemental,
              }[value] || value)
          );
        case SearchKey.subject:
          return normalizeSelectedMetadataItems(
            {
              selected: value,
              metadata: metadata.subjects,
              fromKey: 'urlLabel',
              toKey: 'i18nLabel', // TODO: switch to id when BE is ready
            },
            false
          );
        case SearchKey.product:
          return payload.product;
        case SearchKey.template:
          return this.getTemplatePayload(payload);
        case SearchKey.ageLevel:
          return normalizeSelectedMetadataItems({
            selected: value,
            metadata: metadata.academicLevels,
            fromKey: 'urlLabel',
            toKey: 'i18nLabel', // TODO: switch to id when BE is ready
          });
        case SearchKey.time:
          return normalizeSelectedMetadataItems({
            selected: value,
            metadata: metadata.timing,
            fromKey: 'urlLabel',
            toKey: 'i18nLabel', // TODO: switch to id when BE is ready
          });
        default:
          return value;
      }
    });
    // modify keys
    payload = mapKeys(
      payload,
      (_, key) =>
        ({
          [SearchKey.ageLevel]: 'ag',
          [SearchKey.product]: 'p',
          [SearchKey.subject]: 'sb',
          [SearchKey.time]: 'tm',
          [SearchKey.type]: 'ty',
          [SearchKey.duration]: 'd',
          [SearchKey.template]: 'hasCCXTemplate',
        }[key] || key)
    );

    return {
      ...payload,
      r: entityType,
      offset,
      limit,
    };
  }

  execSearch() {
    [...this.entityType2Action.entries()]
      .filter(([entityType]) => this.searchEntityTypes.includes(entityType))
      .filter(([entityType]) => this.showContent(entityType))
      .forEach(([entityType, action]) => action(this.getSearchPayloadByType(entityType)));
  }
}
